import { Store, GetterTree, MutationTree, ActionTree, Module } from "vuex";

import { RootState } from "..";
import { IUserState } from "../states/user-state";
import { authService } from "../../services/auth.service";
import { IRegistration } from "../../models/interfaces/authentication/registration.model.interface";
import { IRegistrationResponse } from "../../models/interfaces/authentication/registration-response.interface";
import { RegistrationResponseType } from "../../enums/RegistrationResponseType";
import { UserAlert } from "../../models/user-alert";
import { IUserAlert } from "../../models/interfaces/user-alert.interface";
import { ILoginResult } from "../../models/interfaces/authentication/login-result.model.interface";
import { ILogin } from "../../models/interfaces/authentication/login.model.interface";
import { IForgottenPasswordRequest } from "../../models/interfaces/authentication/forgotten-password-request.model.interface";
import { IResetPassword } from "../../models/interfaces/authentication/reset-password.model.interface";
import { IUpdatePassword } from "../../models/interfaces/authentication/update-password.model.interface";
import { IPortalUser } from "../../models/interfaces/authentication/portal-user.model.interface";
import { UpdatePasswordResponseType } from "../../enums/UpdatePasswordResponseType";

export const state: IUserState = {
    token: localStorage.getItem("auth-token") || "",
    user: authService.getCurrentUser(),
    status: "",
    registrationResponse: null,
    loginResponse: null,
    userAlert: new UserAlert(),
    forgottenPasswordResponse: false,
    updatePasswordResponse: false
};

export const getters: GetterTree<IUserState, RootState> = {
    isAuthenticated(state) {
        return !!state.token;
    },
    authStatus(state) {
        return state.status;
    },
    authToken(state) {
        return state.token;
    },
    user(state) {
        return state.user;
    },
    userId(state) {
        if (state.user) {
            return state.user.id;
        }

        return 0;
    },
    isAdmin(state) {
        if (state.user === null) {
            return false;
        }

        return state.user.role && state.user.role.name === "Admin";
    },
    revenueAccess(state, getters) {
        if (state.user === null) {
            return false;
        }

        return state.user.revenueAccess || getters.isAdmin;
    },
    loginResponse(state) {
        return state.loginResponse;
    },
    registrationResponse(state) {
        return state.registrationResponse;
    },
    hasRegistered(state) {
        if (state.registrationResponse === null) {
            return false;
        }

        return state.registrationResponse.success;
    },
    userAlert(state) {
        return state.userAlert;
    },
    forgottenPasswordResponse(state) {
        return state.forgottenPasswordResponse;
    },
    updatePasswordResponse(state) {
        return state.updatePasswordResponse;
    }
};

export const mutations: MutationTree<IUserState> = {
    authToken(state, token: string) {
        state.token = token;
        localStorage.setItem("auth-token", token);
    },
    user(state, user: IPortalUser) {
        state.user = user;
        localStorage.setItem("auth-user", JSON.stringify(user));
    },
    loginResponse(state, login: ILoginResult) {
        state.loginResponse = login;
    },
    registrationResponse(state, registration: IRegistrationResponse) {
        state.registrationResponse = registration;
    },
    userAlertMessage(state, userAlert: IUserAlert) {
        state.userAlert.message = userAlert.message;
        state.userAlert.success = userAlert.success;
        state.userAlert.dismissed = false;
    },
    userAlertMessageDismissed(state) {
        state.userAlert.dismissed = true;
    },
    forgottenPasswordResponse(state, success: boolean) {
        state.forgottenPasswordResponse = success;
    },
    logout(state) {
        localStorage.removeItem("auth-user");
        localStorage.removeItem("auth-token");
        state.user = null;
        state.token = "";
        state.loginResponse = null;
    },
    updatePasswordResponse(state, success: boolean) {
        state.updatePasswordResponse = success;
    }
};

export const actions: ActionTree<IUserState, RootState> = {
    async login({ state, commit, dispatch }, login: ILogin) {
        return new Promise((resolve, reject) => {
            authService.login(login).then((value) => {
                // Store login response.
                commit("loginResponse", value);

                const alertMessage: IUserAlert = new UserAlert();

                if (value.success && value.user !== null) {
                    // Store Auth token.
                    commit("authToken", value.token);
                    commit("user", value.user);
                    dispatch("publisherModule/retrieveUserProperties", value.user.id, { root: true });

                    alertMessage.success = true;
                    alertMessage.message = "Login successful!";
                } else {
                    alertMessage.success = false;

                    if (value.result === "notsetup") {
                        alertMessage.message = "Sorry your account is not linked to a publisher.";
                    } else if (value.result === "pending") {
                        alertMessage.message = "Sorry your account is still pending, you will be notified once your account has been authorised.";
                    } else if (value.result === "notvalidated") {
                        alertMessage.message = "Sorry your email has not yet been validated, please check your emails.";
                    } else if (value.result === "blocked") {
                        alertMessage.message = "Sorry your account has been blocked, please contact for further details.";
                    } else {
                        alertMessage.message = "Sorry something went wrong, please check your details and try again.";
                    }
                }

                commit("userAlertMessage", alertMessage);
                resolve();
            });
        });
    },
    alertDismissed({ state, commit }) {
        commit("userAlertMessageDismissed");
    },
    async register({ state, commit }, registration: IRegistration) {
        return new Promise((resolve, reject) => {
            authService.register(registration).then((value) => {
                commit("registrationResponse", value);
                if (value.success) {
                    commit("userAlertMessageDismissed");
                } else {
                    const alertMessage: IUserAlert = new UserAlert(false);
                    if (value.type === RegistrationResponseType.DuplicateUser) {
                        alertMessage.message = "Registration failed! You have already registered, we are in the process of reviewing your account.";
                    } else {
                        alertMessage.message = "Sorry, something went wrong, please try again later.";
                    }
                    commit("userAlertMessage", alertMessage);
                }
                resolve();
            });
        });
    },
    async forgotPassword({ state, commit }, forgottenPassword: IForgottenPasswordRequest) {
        return new Promise((resolve, reject) => {
            authService.forgottenPasswordRequest(forgottenPassword).then((value) => {
                commit("forgottenPasswordResponse", value);
                const alertMessage: IUserAlert = new UserAlert(true, "You will receive an email shortly. Please follow this instructions to reset your password.");
                commit("userAlertMessage", alertMessage);
                resolve();
            });
        });
    },
    async resetPassword({ state, commit }, resetPassword: IResetPassword) {
        return new Promise((resolve, reject) => {
            authService.resetPasswordRequest(resetPassword).then((value) => {
                const alertMessage: IUserAlert = new UserAlert(value);
                if (value) {
                    commit("forgottenPasswordResponse", value);
                    alertMessage.message = "You have successfully updated your password.";
                } else {
                    alertMessage.message = "Sorry, something went wrong, please try again later.";
                }
                commit("userAlertMessage", alertMessage);
                resolve(value);
            });
        });
    },
    async updatePassword({ state, commit }, updatePassword: IUpdatePassword) {
        return new Promise((resolve, reject) => {
            authService.updatePasswordRequest(updatePassword).then((value) => {
                const alertMessage: IUserAlert = new UserAlert(value.success);
                if (value.success) {
                    commit("updatePasswordResponse", value);
                    alertMessage.message = "You have successfully updated your password.";
                } else {
                    if (value.type === UpdatePasswordResponseType.IncorrectPassword) {
                        alertMessage.message = "The password you entered doesn't match our records.";
                    } else {
                        alertMessage.message = "Sorry, something went wrong, please try again later.";
                    }
                }
                commit("userAlertMessage", alertMessage);
                resolve(value);
            });
        });
    },
    logout({ commit, dispatch }, forced: boolean = false) {
        const alertMessage: IUserAlert = new UserAlert();

        // Clear stored values
        commit("logout");
        dispatch("publisherModule/setSelectedProperty", null, { root: true });

        if (forced) {
            alertMessage.success = false;
            alertMessage.message = "Your session has expired, please login.";
        } else {
            alertMessage.success = true;
            alertMessage.message = "Logout successful!";
        }

        commit("userAlertMessage", alertMessage);
    }
};

export const userModule: Module<IUserState, RootState> = {
    state, getters, mutations, actions, namespaced: true
};
